import React, { useEffect, useMemo, useState } from 'react';

import { Checkbox, Paper } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import PropTypes from 'prop-types';

import { InputField } from 'components/FormHelper/InputField';

import './EventExpertsMultiComboBox.sass';

const SELECT_ALL_OPTION = {
  title: 'Alle auswählen',
  value: '__all__',
};

const NO_MORE_EXPERTS_OPTION = {
  title: 'Es gibt keine weiteren Fachkräfte',
  value: '__none__',
};

const toSafeArray = (arrayLike) => (Array.isArray(arrayLike) ? arrayLike : []);

export const EventExpertsMultiComboBox = ({
  options,
  value,
  onChange,
  onBlur,
  name,
  label,
  disabled,
  maxSelections,
}) => {
  const safeOptions = useMemo(() => options || [], [options]);
  const [isOpen, setIsOpen] = useState(false);
  const [pendingSelectedIds, setPendingSelectedIds] = useState(() => toSafeArray(value));
  const hasSelectableExperts = safeOptions.length > 0;

  useEffect(() => {
    if (!isOpen) {
      setPendingSelectedIds(toSafeArray(value));
    }
  }, [isOpen, value]);

  const selectedOptions = useMemo(() => {
    return safeOptions.filter((option) =>
      pendingSelectedIds.some((selectedValue) => String(option.value) === String(selectedValue))
    );
  }, [safeOptions, pendingSelectedIds]);

  const optionsWithSelectAll = useMemo(() => {
    if (!hasSelectableExperts) {
      return [NO_MORE_EXPERTS_OPTION];
    }

    return [SELECT_ALL_OPTION, ...safeOptions];
  }, [safeOptions, hasSelectableExperts]);

  const isAllSelected = hasSelectableExperts && selectedOptions.length === safeOptions.length;

  const normalizeSelection = (selectedIds) => {
    const selectedOptionsSafe = safeOptions.filter((option) =>
      selectedIds.some((selectedId) => String(option.value) === String(selectedId))
    );
    const cappedOptions =
      typeof maxSelections === 'number' && maxSelections >= 0
        ? selectedOptionsSafe.slice(0, maxSelections)
        : selectedOptionsSafe;
    return cappedOptions.map((option) => option.value);
  };

  const emitSelectedValues = () => {
    if (!hasSelectableExperts) {
      setIsOpen(false);
      return;
    }

    onChange(name, normalizeSelection(pendingSelectedIds));
    setIsOpen(false);
  };

  const setPendingFromOptions = (selectedOptionsRaw) => {
    const selectedIds = (selectedOptionsRaw || [])
      .filter(
        (option) =>
          option.value !== SELECT_ALL_OPTION.value && option.value !== NO_MORE_EXPERTS_OPTION.value
      )
      .map((option) => option.value);
    setPendingSelectedIds(normalizeSelection(selectedIds));
  };

  const handleChange = (_, selected, reason, details) => {
    const interactedOption = details?.option;

    if (interactedOption?.value === NO_MORE_EXPERTS_OPTION.value) {
      return;
    }

    if (interactedOption?.value === SELECT_ALL_OPTION.value) {
      if (isAllSelected) {
        setPendingSelectedIds([]);
        return;
      }

      setPendingSelectedIds(normalizeSelection(safeOptions.map((option) => option.value)));
      return;
    }

    if (reason === 'clear') {
      setPendingSelectedIds([]);
      return;
    }

    setPendingFromOptions(selected);
  };

  const handleOpen = () => {
    setPendingSelectedIds(toSafeArray(value));
    setIsOpen(true);
  };

  const renderDropdownPaper = (paperProps) => {
    return (
      <Paper {...paperProps}>
        {paperProps.children}
        <div className="event-experts-multi-combo-box__footer">
          <button
            type="button"
            className="event-experts-multi-combo-box__confirm-btn"
            onMouseDown={(event) => event.preventDefault()}
            onClick={emitSelectedValues}
            disabled={!hasSelectableExperts}>
            Auswahl bestätigen
          </button>
        </div>
      </Paper>
    );
  };

  return (
    <Autocomplete
      open={isOpen}
      onOpen={handleOpen}
      onClose={() => setIsOpen(false)}
      multiple
      disableCloseOnSelect
      disableClearable
      options={optionsWithSelectAll}
      value={selectedOptions}
      disabled={disabled}
      PaperComponent={renderDropdownPaper}
      getOptionLabel={(option) => option.title}
      getOptionSelected={(option, optionValue) =>
        String(option.value) === String(optionValue.value)
      }
      getOptionDisabled={(option) => option.value === NO_MORE_EXPERTS_OPTION.value}
      onChange={handleChange}
      onBlur={onBlur}
      renderOption={(option, { selected }) => {
        if (option.value === NO_MORE_EXPERTS_OPTION.value) {
          return (
            <div className="event-experts-multi-combo-box__option">
              <span>{option.title}</span>
            </div>
          );
        }

        const optionSelected = option.value === SELECT_ALL_OPTION.value ? isAllSelected : selected;

        return (
          <div className="event-experts-multi-combo-box__option">
            <Checkbox checked={optionSelected} color="primary" />
            <span>{option.title}</span>
          </div>
        );
      }}
      renderTags={() => null}
      renderInput={(params) => (
        <InputField
          {...params}
          fullWidth
          label={label}
          name={name}
          value=""
          disabled={disabled}
          inputProps={{
            ...params.inputProps,
            autoComplete: 'off',
          }}
        />
      )}
    />
  );
};

EventExpertsMultiComboBox.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  maxSelections: PropTypes.number,
};

EventExpertsMultiComboBox.defaultProps = {
  onBlur: undefined,
  disabled: false,
  maxSelections: undefined,
};
